import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { toast } from 'sonner';
import { UserPlus, ArrowRight, Eye, EyeOff } from 'lucide-react';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    password: '',
    password_confirm: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.password_confirm) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      const { password_confirm, ...registerData } = formData;
      const user = await register(registerData);
      toast.success(`Willkommen bei FarbWelt, ${user.first_name}! Ihre Kundennummer: ${user.customer_number}`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center py-12 px-6">
      <div className="w-full max-w-xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <picture>
              <source srcSet="/logo-new.webp" type="image/webp" />
              <img 
                src="/logo-new.png" 
                alt="FarbWelt Maler Express Service Logo" 
                className="h-20 w-auto object-contain"
                fetchPriority="high"
                decoding="sync"
              />
            </picture>
          </Link>
        </div>

        <Card className="shadow-lg" data-testid="register-card">
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 bg-[#9B1B30]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-7 h-7 text-[#9B1B30]" />
            </div>
            <CardTitle className="font-barlow text-2xl">Konto erstellen</CardTitle>
            <CardDescription className="font-manrope">
              Registrieren Sie sich für ein Kundenkonto
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="font-barlow font-bold text-sm">Vorname *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="register-firstname"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="font-barlow font-bold text-sm">Nachname *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="register-lastname"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="font-barlow font-bold text-sm">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="font-barlow font-bold text-sm">Telefon *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="register-phone"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="street" className="font-barlow font-bold text-sm">Straße & Hausnummer *</Label>
                <AddressAutocomplete
                  value={formData.street}
                  onChange={(val) => setFormData({ ...formData, street: val })}
                  onAddressSelect={(addr) => {
                    setFormData({
                      ...formData,
                      street: addr.street,
                      postal_code: addr.postcode || formData.postal_code,
                      city: addr.city || formData.city
                    });
                  }}
                  placeholder="Straße und Hausnummer eingeben..."
                  className="mt-1 [&_input]:bg-white [&_input]:border-gray-300 [&_input]:text-gray-900 [&_input]:placeholder-gray-400"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postal_code" className="font-barlow font-bold text-sm">PLZ *</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="register-plz"
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="font-barlow font-bold text-sm">Ort *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="register-city"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password" className="font-barlow font-bold text-sm">Passwort *</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pr-10"
                      placeholder="Min. 8 Zeichen"
                      required
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="password_confirm" className="font-barlow font-bold text-sm">Passwort bestätigen *</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password_confirm"
                      type={showPasswordConfirm ? "text" : "password"}
                      value={formData.password_confirm}
                      onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                      className="pr-10"
                      required
                      data-testid="register-password-confirm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 font-manrope">
                Mit der Registrierung akzeptieren Sie unsere{' '}
                <Link to="/datenschutz" className="text-[#9B1B30] hover:underline">Datenschutzerklärung</Link> und{' '}
                <Link to="/agb" className="text-[#9B1B30] hover:underline">AGB</Link>.
              </p>

              <Button 
                type="submit" 
                className="btn-primary w-full mt-6"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? 'Wird erstellt...' : 'Konto erstellen'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="font-manrope text-sm text-gray-600">
                Bereits ein Konto?{' '}
                <Link to="/login" className="text-[#9B1B30] font-medium hover:underline">
                  Jetzt anmelden
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center mt-6 font-manrope text-sm text-gray-500">
          <Link to="/" className="hover:text-[#9B1B30]">← Zurück zur Startseite</Link>
        </p>
      </div>
    </div>
  );
}
